import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useSearchParams, useNavigate } from 'react-router-dom';
import {
  ArrowLeftIcon,
  HomeIcon,
  CalendarIcon,
  ClipboardDocumentListIcon,
  BellIcon,
  ClipboardDocumentCheckIcon,
  DocumentTextIcon,
  Cog6ToothIcon,
  CheckIcon,
  PencilIcon,
  ChevronRightIcon,
  DocumentDuplicateIcon,
  VideoCameraIcon,
  PencilSquareIcon,
  BanknotesIcon,
  ShieldCheckIcon,
  XMarkIcon,
  UserCircleIcon,
  InformationCircleIcon,
} from '@heroicons/react/24/outline';
import { Spinner } from '../components/ui';
import ClientNotesTab from '../components/ClientNotesTab';
import SessionSummaryPanel from '../components/SessionSummaryPanel';
import FollowUpsTab from '../components/FollowUpsTab';
import ActionItemsPanel from '../components/ActionItemsPanel';
import QuestionnairesTab from '../components/QuestionnairesTab';
import ClientFormsTab from '../components/ClientFormsTab';
import { config } from '../config/environment';
import { endpoints } from '../config/endpoints';
import { getUserTimezone } from '../services/timezoneUtils';
import { type ApiCountry } from '../components/AddressFields';
import { useCountryStates } from '../hooks/useCountryStates';
import ReactPhoneInput from 'react-phone-input-2';
import 'react-phone-input-2/lib/style.css';
import {
  listClientFiles, ClientFolderPacket,
  listDocuments, DocumentItem, createPacket,
  listTemplateTags, DocumentTemplateTag,
  getDocumentViewUrl, ViewUrlResponse,
} from '../services/documentService';
import DocumentViewerPanel from '../components/DocumentViewerPanel';
import SessionDetailModal from '../components/SessionDetailModal';
import NotificationPrefsPanel, { type EventDef } from '../components/NotificationPrefsPanel';

// ── Types ─────────────────────────────────────────────────────────────────────

type Section = 'overview' | 'sessions' | 'action-items' | 'nudges' | 'checkins' | 'forms' | 'documents' | 'notes' | 'settings';

interface ClientProfile {
  client_id: string;
  relationship_id: number;
  first_name: string;
  last_name: string;
  email: string;
  phone: string;
  date_of_birth: string;
  emergency_contact_name: string;
  emergency_contact_phone: string;
  street_address: string;
  city: string;
  state_province: string;
  postal_code: string;
  country: string;
  marital_status: string;
  dependents: string;
  occupation: string;
  employer: string;
  industry: string;
  bio: string;
  start_date: string | null;
  ai_consent: boolean | null;
  ai_consent_at: string | null;
  privacy_policy_accepted: boolean | null;
  privacy_policy_accepted_at: string | null;
  terms_accepted: boolean | null;
  terms_accepted_at: string | null;
  is_email_verified: boolean | null;
  is_phone_verified: boolean | null;
  has_signed_agreement: boolean | null;
}

interface Appointment {
  id: string;
  scheduled_at: string;
  meeting_name: string;
  status: string;
  summary_available?: boolean;
  has_summary?: boolean;
  actual_duration_minutes?: number | null;
  meeting_url?: string | null;
  payment_status?: {
    status: string;
    pricing_rate_cents: number | null;
    payment: {
      id: string | null;
      amount_cents: number | null;
      currency: string | null;
      paid_at: string | null;
      payment_method: string | null;
    } | null;
  } | null;
}

interface ActionItem {
  id: string;
  title: string;
  action_item_type: string;
  status: string;
  due_date?: string | null;
  priority?: string | null;
  meeting_name?: string | null;
  coach_approved: boolean;
  assigned_to?: string | null;
}

interface OutstandingPayment {
  kind: string;
  label?: string;
  appointment_id?: string;
  amount_cents: number;
  currency: string;
  due_date?: string | null;
  payment_status?: string;
  meeting_name?: string;
  scheduled_at?: string;
}

interface NotifPref {
  event_type: string;
  channel: string;
  email_override: string | null;
}

interface ExtraSection {
  id: string;
  display_name: string;
  instructions: string;
  enabled: boolean;
  client_visible: boolean;
}

// ── Constants ─────────────────────────────────────────────────────────────────

const CLIENT_NOTIF_EVENTS: EventDef[] = [
  { key: 'session_reminder',     label: 'Session Reminders',    desc: 'Reminders before each upcoming coaching session' },
  { key: 'session_notes_shared', label: 'Notes / Action Items', desc: 'Coach shares session notes or action items' },
  { key: 'nudge',                label: 'Nudges',               desc: 'Coaching reminders sent to keep on track' },
  { key: 'form_assigned',        label: 'Form assigned',        desc: 'A form or questionnaire is assigned' },
  { key: 'form_due_reminder',    label: 'Form due reminder',    desc: 'Reminder that an assigned form is due soon' },
  { key: 'invoice_sent',         label: 'Invoice sent',         desc: 'An invoice for a session or package' },
  { key: 'payment_confirmation', label: 'Payment confirmation', desc: 'A payment has been processed' },
  { key: 'payment_failure',      label: 'Payment failure',      desc: 'A payment attempt was unsuccessful' },
];

const NAV_ITEMS: { key: Section; label: string; Icon: React.ElementType }[] = [
  { key: 'overview',      label: 'Overview',      Icon: HomeIcon },
  { key: 'sessions',      label: 'Sessions',      Icon: CalendarIcon },
  { key: 'action-items',  label: 'Action Items',  Icon: ClipboardDocumentListIcon },
  { key: 'notes',         label: 'Notes',         Icon: PencilSquareIcon },
  { key: 'nudges',        label: 'Nudges',        Icon: BellIcon },
  { key: 'checkins',      label: 'Check-ins',     Icon: ClipboardDocumentCheckIcon },
  { key: 'forms',         label: 'Forms',         Icon: DocumentDuplicateIcon },
  { key: 'documents',     label: 'Documents',     Icon: DocumentTextIcon },
  { key: 'settings',      label: 'Settings',      Icon: Cog6ToothIcon },
];

// ── Helpers ───────────────────────────────────────────────────────────────────

function initials(name: string) {
  return name.split(' ').map(p => p[0]).join('').slice(0, 2).toUpperCase();
}

function authHeaders() {
  return {
    'Content-Type': 'application/json',
    Authorization: `Bearer ${localStorage.getItem('authToken') || ''}`,
  };
}

function getCoachId(): string {
  const ud = JSON.parse(localStorage.getItem('userData') || '{}');
  return ud.coach_id || ud.id || '';
}

// ── Sub-components ────────────────────────────────────────────────────────────

const ProfileField: React.FC<{
  label: string;
  value: string;
  field: keyof ClientProfile;
  type?: string;
  multiline?: boolean;
  onChange: (f: keyof ClientProfile, v: string) => void;
}> = ({ label, value, field, type = 'text', multiline = false, onChange }) => (
  <div>
    <label className="block text-xs font-medium text-gray-400 uppercase tracking-wide mb-1">{label}</label>
    {multiline ? (
      <textarea
        rows={3}
        value={value}
        onChange={e => onChange(field, e.target.value)}
        className="w-full text-sm text-gray-200 bg-dark-hover border border-dark-border rounded-lg px-3 py-2 focus:outline-none focus:border-primary-500 resize-none"
      />
    ) : (
      <input
        type={type}
        value={value}
        onChange={e => onChange(field, e.target.value)}
        className="w-full text-sm text-gray-200 bg-dark-hover border border-dark-border rounded-lg px-3 py-2 focus:outline-none focus:border-primary-500"
      />
    )}
  </div>
);

// ── Main Component ─────────────────────────────────────────────────────────────

export default function ClientDetailPage() {
  const { clientId } = useParams<{ clientId: string }>();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const timezone = getUserTimezone();

  const initialSection = (searchParams.get('section') as Section) || 'overview';
  const [section, setSection] = useState<Section>(initialSection);
  const [profile, setProfile] = useState<ClientProfile | null>(null);
  const [loadingProfile, setLoadingProfile] = useState(true);

  // Sessions
  const [appointments, setAppointments] = useState<Appointment[]>([]);
  const [loadingAppts, setLoadingAppts] = useState(false);
  const [sessionsOpen, setSessionsOpen] = useState<Record<string, boolean>>({ today: true, week: true });
  const [summaryAppt, setSummaryAppt] = useState<Appointment | null>(null);

  // Action items (for overview + action-items tab)
  const [allActionItems, setAllActionItems] = useState<ActionItem[]>([]);
  const [loadingItems, setLoadingItems] = useState(false);
  const [actionItemsTypeFilter, setActionItemsTypeFilter] = useState<string | null>(null);


  // Outstanding payments (for overview)
  const [outstanding, setOutstanding] = useState<OutstandingPayment[]>([]);
  const [loadingOutstanding, setLoadingOutstanding] = useState(false);

  // Revenue summary (sessions + packages, all-time)
  const [revenueSummary, setRevenueSummary] = useState<{ total_revenue_cents: number; currency: string } | null>(null);
  const [loadingRevenue, setLoadingRevenue] = useState(false);

  // Consent quick-save (Client Info card)
  const [consentSaving, setConsentSaving] = useState<'ai_consent' | 'terms_accepted' | null>(null);
  const [consentSavedKey, setConsentSavedKey] = useState<'ai_consent' | 'terms_accepted' | null>(null);
  const [aiConsentPending, setAiConsentPending] = useState<boolean | null>(null);

  // Settings – sub-section navigation (null = card grid landing page)
  const [settingsSubSection, setSettingsSubSection] = useState<'profile' | 'notes' | 'notifications' | 'privacy' | null>(null);

  // Settings – Privacy & Data (delete client)
  const [showDeleteClientModal, setShowDeleteClientModal] = useState(false);
  const [deleteClientConfirm, setDeleteClientConfirm] = useState('');
  const [deletingClient, setDeletingClient] = useState(false);
  const [deleteClientError, setDeleteClientError] = useState('');
  const [downloadingClientData, setDownloadingClientData] = useState(false);
  const [downloadClientDataError, setDownloadClientDataError] = useState('');

  // Settings – Profile edit
  const [editProfile, setEditProfile] = useState<ClientProfile | null>(null);
  const [profileEditing, setProfileEditing] = useState(false);
  const [profileDirty, setProfileDirty] = useState(false);
  const [profileSaving, setProfileSaving] = useState(false);
  const [profileSaved, setProfileSaved] = useState(false);
  const [profileError, setProfileError] = useState<string | null>(null);
  const [apiCountries, setApiCountries] = useState<ApiCountry[]>([]);
  // DB-backed state/province options for the selected country (E1.8 / CSECR-64).
  const { states: stateOptions } = useCountryStates(editProfile?.country || '');
  const [phoneError, setPhoneError] = useState<string | null>(null);

  // Settings – Custom Notes
  const [extraSections, setExtraSections] = useState<ExtraSection[]>([]);
  const [notesSaving, setNotesSaving] = useState(false);
  const [notesSaved, setNotesSaved] = useState(false);
  const [notesError, setNotesError] = useState<string | null>(null);

  // Settings – Notifications
  const [notifPrefs, setNotifPrefs] = useState<NotifPref[]>([]);
  const [notifSaving, setNotifSaving] = useState(false);
  const [notifSaved, setNotifSaved] = useState(false);
  const [notifError, setNotifError] = useState<string | null>(null);

  // Session detail modal
  const [editingAppt, setEditingAppt] = useState<Appointment | null>(null);

  // Documents
  const [docPackets, setDocPackets] = useState<ClientFolderPacket[]>([]);
  const [docsLoading, setDocsLoading] = useState(false);
  const [docsError, setDocsError] = useState<string | null>(null);
  const [docViewer, setDocViewer] = useState<ViewUrlResponse | null>(null);
  const [docViewLoading, setDocViewLoading] = useState<Set<string>>(new Set());
  const [sendWelcomeModal, setSendWelcomeModal] = useState(false);
  const [welcomeDocs, setWelcomeDocs] = useState<DocumentItem[]>([]);
  const [welcomeTitle, setWelcomeTitle] = useState('Welcome Packet');
  const [welcomeSelectedIds, setWelcomeSelectedIds] = useState<Set<string>>(new Set());
  const [welcomeSending, setWelcomeSending] = useState(false);
  const [welcomeSendError, setWelcomeSendError] = useState('');
  const [welcomeSendSuccess, setWelcomeSendSuccess] = useState('');
  const [welcomeTagValues, setWelcomeTagValues] = useState<Record<string, string>>({});

  // ── Load profile on mount ──────────────────────────────────────────────────

  useEffect(() => {
    if (!clientId) return;
    const coachId = getCoachId();
    setLoadingProfile(true);
    Promise.all([
      fetch(`${config.apiBaseUrl}/coaches/${coachId}/clients/${clientId}/profile`, { headers: authHeaders() }).then(r => r.json()),
      fetch(`${config.apiBaseUrl}/countries`).then(r => r.ok ? r.json() : []).catch(() => []),
    ]).then(([data, countries]) => {
      setProfile(data);
      setEditProfile(data);
      setApiCountries(countries);
    }).catch(() => {}).finally(() => setLoadingProfile(false));
  }, [clientId]);

  // ── Load appointments (once, with payment data) ───────────────────────────

  useEffect(() => {
    if (!clientId) return;
    setLoadingAppts(true);
    fetch(`${config.apiBaseUrl}${endpoints.main.clients.sessions(clientId)}?include_payments=true`, { headers: authHeaders() })
      .then(r => r.json())
      .then(d => setAppointments(d.appointments || []))
      .catch(() => setAppointments([]))
      .finally(() => setLoadingAppts(false));
  }, [clientId]);

  // ── Load action items + outstanding on overview/action-items ───────────────

  useEffect(() => {
    if (!clientId) return;
    setLoadingItems(true);
    fetch(`${config.apiBaseUrl}${endpoints.main.actionItems.byClientCoach(clientId)}`, { headers: authHeaders() })
      .then(r => r.json())
      .then(d => {
        const items: ActionItem[] = d.action_items || [];
        setAllActionItems(items);
      })
      .catch(() => {})
      .finally(() => setLoadingItems(false));
  }, [clientId]);

  useEffect(() => {
    if (!clientId) return;
    setLoadingOutstanding(true);
    fetch(`${config.apiBaseUrl}${endpoints.main.coaches.clientOutstandingPayments(clientId)}`, { headers: authHeaders() })
      .then(r => r.ok ? r.json() : [])
      .then(d => setOutstanding(d || []))
      .catch(() => setOutstanding([]))
      .finally(() => setLoadingOutstanding(false));
  }, [clientId]);

  useEffect(() => {
    if (!clientId) return;
    setLoadingRevenue(true);
    fetch(`${config.apiBaseUrl}${endpoints.main.coaches.clientRevenueSummary(clientId)}`, { headers: authHeaders() })
      .then(r => r.ok ? r.json() : null)
      .then(d => setRevenueSummary(d))
      .catch(() => setRevenueSummary(null))
      .finally(() => setLoadingRevenue(false));
  }, [clientId]);

  // ── Load settings data when settings tab is selected ──────────────────────

  useEffect(() => {
    if (section !== 'settings' || !clientId || !profile) return;
    const relIdVal = Number(searchParams.get('rel')) || profile.relationship_id;

    // Custom Notes
    fetch(`${config.apiBaseUrl}${endpoints.main.coaches.clientAiConfig(relIdVal)}`, { headers: authHeaders() })
      .then(r => r.ok ? r.json() : Promise.reject())
      .then(d => setExtraSections(d.extra_sections || []))
      .catch(() => {});

    // Notification prefs
    fetch(`${config.apiBaseUrl}${endpoints.main.coaches.clientNotifPrefs(clientId)}`, { headers: authHeaders() })
      .then(r => r.ok ? r.json() : Promise.reject())
      .then((prefs: NotifPref[]) => setNotifPrefs(prefs))
      .catch(() => {});
  }, [section, clientId, profile, searchParams]);

  // ── Load documents when documents tab is selected ──────────────────────────

  useEffect(() => {
    if (section !== 'documents' || !profile) return;
    const relIdVal = Number(searchParams.get('rel')) || profile.relationship_id;
    setDocsLoading(true);
    listClientFiles(relIdVal)
      .then(setDocPackets)
      .catch(() => setDocsError('Could not load documents.'))
      .finally(() => setDocsLoading(false));
  }, [section, profile, searchParams]);

  // ── Handlers ───────────────────────────────────────────────────────────────

  const handleProfileFieldChange = useCallback((field: keyof ClientProfile, value: string) => {
    setEditProfile(p => p ? { ...p, [field]: value } : p);
    setProfileDirty(true);
    setProfileSaved(false);
  }, []);

  const handleSaveProfile = async () => {
    if (!editProfile || !clientId) return;
    const coachId = getCoachId();
    if (!coachId) {
      setProfileError('Session expired. Redirecting to login...');
      setTimeout(() => navigate('/login?role=coach'), 1500);
      return;
    }
    setProfileSaving(true);
    setProfileError(null);
    try {
      const res = await fetch(
        `${config.apiBaseUrl}/coaches/${coachId}/clients/${clientId}/profile`,
        { method: 'PATCH', headers: authHeaders(), body: JSON.stringify(editProfile) }
      );
      if (res.status === 401) {
        setProfileError('Session expired. Redirecting to login...');
        setTimeout(() => navigate('/login?role=coach'), 1500);
        return;
      }
      if (!res.ok) throw new Error('Save failed');
      const updated = await res.json();
      setProfile(p => p ? { ...p, ...updated } : updated);
      setEditProfile(p => p ? { ...p, ...updated } : updated);
      setProfileDirty(false);
      setProfileSaved(true);
      setTimeout(() => setProfileSaved(false), 3000);
    } catch (e: any) {
      setProfileError(e.message || 'Save failed');
    } finally {
      setProfileSaving(false);
    }
  };

  const handleSaveNotes = async () => {
    if (!profile) return;
    const relIdVal = Number(searchParams.get('rel')) || profile.relationship_id;
    setNotesSaving(true);
    setNotesError(null);
    try {
      const res = await fetch(
        `${config.apiBaseUrl}${endpoints.main.coaches.clientAiConfig(relIdVal)}`,
        { method: 'PUT', headers: authHeaders(), body: JSON.stringify({ extra_sections: extraSections }) }
      );
      if (!res.ok) throw new Error('Save failed');
      const d = await res.json();
      setExtraSections(d.extra_sections || []);
      setNotesSaved(true);
      setTimeout(() => setNotesSaved(false), 3000);
    } catch (e: any) {
      setNotesError(e.message || 'Save failed');
    } finally {
      setNotesSaving(false);
    }
  };

  const handleSaveNotif = async () => {
    if (!clientId) return;
    setNotifSaving(true);
    setNotifError(null);
    try {
      const res = await fetch(
        `${config.apiBaseUrl}${endpoints.main.coaches.clientNotifPrefs(clientId)}`,
        { method: 'PATCH', headers: authHeaders(), body: JSON.stringify(notifPrefs) }
      );
      if (!res.ok) throw new Error('Save failed');
      const updated: NotifPref[] = await res.json();
      setNotifPrefs(updated);
      setNotifSaved(true);
      setTimeout(() => setNotifSaved(false), 3000);
    } catch (e: any) {
      setNotifError(e.message || 'Save failed');
    } finally {
      setNotifSaving(false);
    }
  };

  const handleDeleteClient = async () => {
    if (!clientId) return;
    setDeletingClient(true);
    setDeleteClientError('');
    try {
      const coachId = getCoachId();
      const res = await fetch(
        `${config.apiBaseUrl}${endpoints.main.coaches.deleteClient(coachId, clientId)}`,
        { method: 'DELETE', headers: authHeaders() }
      );
      if (!res.ok) {
        const d = await res.json().catch(() => ({}));
        throw new Error(d.detail || 'Deletion failed. Please try again.');
      }
      navigate('/dashboard/coach');
    } catch (err: any) {
      setDeleteClientError(err.message || 'Something went wrong. Please try again.');
      setDeletingClient(false);
    }
  };

  const handleDownloadClientData = async () => {
    if (!clientId) return;
    setDownloadingClientData(true);
    setDownloadClientDataError('');
    try {
      const coachId = getCoachId();
      const res = await fetch(
        `${config.apiBaseUrl}${endpoints.main.coaches.exportClientData(coachId, clientId)}`,
        { headers: authHeaders() }
      );
      if (!res.ok) throw new Error('Export failed. Please try again.');
      const blob = await res.blob();
      const disposition = res.headers.get('content-disposition') || '';
      const match = disposition.match(/filename="([^"]+)"/);
      const filename = match ? match[1] : 'coachsight-client-export.json';
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      a.click();
      URL.revokeObjectURL(url);
    } catch (err: any) {
      setDownloadClientDataError(err.message || 'Download failed. Please try again.');
    } finally {
      setDownloadingClientData(false);
    }
  };

  const patchConsentField = async (key: 'ai_consent' | 'terms_accepted', value: boolean) => {
    if (!profile || !clientId) return;
    const coachId = getCoachId();
    setConsentSaving(key);
    try {
      const res = await fetch(
        `${config.apiBaseUrl}/coaches/${coachId}/clients/${clientId}/profile`,
        { method: 'PATCH', headers: authHeaders(), body: JSON.stringify({ ...profile, [key]: value }) }
      );
      if (!res.ok) return;
      const updated = await res.json();
      setProfile(p => p ? { ...p, ...updated } : p);
      setConsentSavedKey(key);
      setTimeout(() => setConsentSavedKey(null), 2000);
    } finally {
      setConsentSaving(null);
    }
  };

  // ── Derived data ───────────────────────────────────────────────────────────

  const now = new Date();
  const startOfDay = new Date(now); startOfDay.setHours(0, 0, 0, 0);
  const endOfDay   = new Date(now); endOfDay.setHours(23, 59, 59, 999);
  const endOfWeek  = new Date(now); endOfWeek.setDate(now.getDate() + (6 - now.getDay())); endOfWeek.setHours(23, 59, 59, 999);

  const todaySessions   = appointments.filter(a => { const d = new Date(a.scheduled_at); return d >= startOfDay && d <= endOfDay; });
  const thisWeekSessions = appointments.filter(a => { const d = new Date(a.scheduled_at); return d > endOfDay && d <= endOfWeek; });
  const futureSessions  = appointments.filter(a => new Date(a.scheduled_at) > endOfWeek).sort((a, b) => new Date(a.scheduled_at).getTime() - new Date(b.scheduled_at).getTime());
  const pastSessions    = appointments.filter(a => new Date(a.scheduled_at) < startOfDay).sort((a, b) => new Date(b.scheduled_at).getTime() - new Date(a.scheduled_at).getTime());

  const sessionsThisWeek = todaySessions.length + thisWeekSessions.length;

  const revenueTotal = revenueSummary?.total_revenue_cents ?? 0;
  const revenueCurrency = revenueSummary?.currency ?? 'USD';

  const sessionGroups = [
    { key: 'today',    label: 'Today',     sessions: todaySessions },
    { key: 'week',     label: 'This Week', sessions: thisWeekSessions },
    { key: 'future',   label: 'Future',    sessions: futureSessions },
    { key: 'past',     label: 'Past',      sessions: pastSessions },
  ].filter(g => g.sessions.length > 0);

  const clientName = profile ? `${profile.first_name} ${profile.last_name}`.trim() : '';
  const relIdVal = Number(searchParams.get('rel')) || profile?.relationship_id || 0;

  if (loadingProfile) {
    return (
      <div className="flex h-screen bg-dark-canvas items-center justify-center">
        <Spinner size="lg" />
      </div>
    );
  }

  if (!profile) {
    return (
      <div className="flex h-screen bg-dark-canvas items-center justify-center">
        <div className="text-center">
          <p className="text-white font-medium">Client not found</p>
          <button onClick={() => navigate('/dashboard/coach')} className="mt-3 text-sm text-primary-400 hover:underline">Back to dashboard</button>
        </div>
      </div>
    );
  }

  // ── Section renderers ──────────────────────────────────────────────────────

  function renderOverview() {
    if (!profile) return null;
    const fmtCurrency = (cents: number, currency: string) =>
      new Intl.NumberFormat('en-US', { style: 'currency', currency: currency || 'USD', maximumFractionDigits: 0 }).format(cents / 100);

    return (
      <div className="p-6 space-y-5">
        {/* ── Stat cards ── */}
        <div className="grid grid-cols-3 gap-4">
          <StatCard
            icon={<CalendarIcon className="w-6 h-6" />}
            value={loadingAppts ? '—' : String(sessionsThisWeek)}
            label="Sessions this week"
          />
          {/* Action items pending — breakdown by type */}
          <div className="bg-[#1e1a0e] rounded-2xl p-6 flex flex-col gap-3">
            <div className="text-gray-500"><ClipboardDocumentListIcon className="w-6 h-6" /></div>
            <div>
              <p className="text-sm text-gray-500 mb-2">Action items pending</p>
              {loadingItems ? (
                <Spinner size="sm" />
              ) : (
                <div className="grid grid-cols-2 gap-x-4 gap-y-1">
                  {([
                    { key: 'task',     label: 'Tasks' },
                    { key: 'form',     label: 'Forms' },
                    { key: 'check-in', label: 'Check-ins' },
                    { key: 'document', label: 'Docs' },
                  ] as const).map(({ key, label }) => {
                    const n = allActionItems.filter(i =>
                      i.coach_approved &&
                      (i.action_item_type || 'task') === key &&
                      (i.status === 'pending' || i.status === 'in_progress')
                    ).length;
                    return (
                      <div key={key} className="flex items-center justify-between gap-2">
                        <span className="text-xs text-gray-500">{label}</span>
                        <button
                          onClick={() => { setActionItemsTypeFilter(key); setSection('action-items'); }}
                          className={`text-sm font-bold transition-colors ${n > 0 ? 'text-amber-400 hover:text-amber-300' : 'text-gray-600 hover:text-gray-400'}`}
                          title={`View ${label} action items`}
                        >
                          {n}
                        </button>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
          <StatCard
            icon={<BanknotesIcon className="w-6 h-6" />}
            value={loadingRevenue ? '—' : fmtCurrency(revenueTotal, revenueCurrency)}
            label="All-time revenue"
            accent="green"
            tooltip="Total collected from this client across all sessions and package payments."
          />
        </div>

        {/* ── Two-column body: Client Info + Sessions ── */}
        <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">

          {/* Client Info card */}
          <div className="bg-dark-card border border-dark-border rounded-xl overflow-hidden">
            <div className="px-5 py-4 border-b border-dark-border flex items-center justify-between">
              <h3 className="text-sm font-semibold text-gray-200">Client Info</h3>
              <button onClick={() => { setSection('settings'); setProfileEditing(true); setSettingsSubSection('profile'); }} className="text-xs text-primary-400 hover:text-primary-300">
                Edit Profile →
              </button>
            </div>
            <div className="p-4 space-y-2.5">
              <div className="flex items-center gap-3">
                <span className="text-xs text-gray-500 w-16 flex-shrink-0">Name</span>
                <span className="text-sm text-gray-200">{clientName}</span>
              </div>
              <div className="flex items-center gap-3">
                <span className="text-xs text-gray-500 w-16 flex-shrink-0">Email</span>
                <span className="text-sm text-gray-200 truncate">{profile.email}</span>
              </div>
              {profile.phone && (
                <div className="flex items-center gap-3">
                  <span className="text-xs text-gray-500 w-16 flex-shrink-0">Phone</span>
                  <span className="text-sm text-gray-200">{profile.phone}</span>
                </div>
              )}
              {profile.start_date && (
                <div className="flex items-center gap-3">
                  <span className="text-xs text-gray-500 w-16 flex-shrink-0">Since</span>
                  <span className="text-sm text-gray-200">
                    {new Date(profile.start_date).toLocaleDateString(undefined, { month: 'short', year: 'numeric' })}
                  </span>
                </div>
              )}
              <div className="border-t border-dark-border pt-3 space-y-3 mt-1">
                {/* AI Consent */}
                <div>
                  <div className="flex items-center justify-between">
                    <span className="text-xs text-gray-400">AI consent</span>
                    <div className="flex items-center gap-2">
                      {consentSaving === 'ai_consent' && <Spinner size="sm" />}
                      {consentSavedKey === 'ai_consent' && <CheckIcon className="w-3.5 h-3.5 text-green-400" />}
                      <button
                        onClick={() => setAiConsentPending(profile.ai_consent ? false : true)}
                        disabled={consentSaving === 'ai_consent'}
                        className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors disabled:opacity-50 ${profile.ai_consent ? 'bg-green-500' : 'bg-gray-600'}`}
                      >
                        <span className={`inline-block h-3.5 w-3.5 transform rounded-full bg-white transition-transform ${profile.ai_consent ? 'translate-x-4' : 'translate-x-1'}`} />
                      </button>
                    </div>
                  </div>
                  {aiConsentPending !== null && (
                    <div className="mt-2 bg-amber-900/20 border border-amber-700/30 rounded-lg p-2.5 space-y-2">
                      <p className="text-xs text-amber-300 font-medium">
                        {aiConsentPending ? 'Enable AI recording for this client?' : 'Disable AI recording for this client?'}
                      </p>
                      <div className="flex gap-2">
                        <button
                          onClick={() => { patchConsentField('ai_consent', aiConsentPending); setAiConsentPending(null); }}
                          className="px-2.5 py-1 bg-amber-600 hover:bg-amber-500 text-white rounded text-xs font-medium"
                        >
                          Confirm
                        </button>
                        <button
                          onClick={() => setAiConsentPending(null)}
                          className="px-2.5 py-1 text-gray-400 hover:text-gray-200 rounded text-xs"
                        >
                          Cancel
                        </button>
                      </div>
                    </div>
                  )}
                </div>
                {/* Terms of Use */}
                <div className="flex items-center justify-between">
                  <span className="text-xs text-gray-400">Terms of Use</span>
                  <div className="flex items-center gap-2">
                    {consentSaving === 'terms_accepted' && <Spinner size="sm" />}
                    {consentSavedKey === 'terms_accepted' && <CheckIcon className="w-3.5 h-3.5 text-green-400" />}
                    <button
                      onClick={() => patchConsentField('terms_accepted', !profile.terms_accepted)}
                      disabled={consentSaving === 'terms_accepted'}
                      className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors disabled:opacity-50 ${profile.terms_accepted ? 'bg-green-500' : 'bg-gray-600'}`}
                    >
                      <span className={`inline-block h-3.5 w-3.5 transform rounded-full bg-white transition-transform ${profile.terms_accepted ? 'translate-x-4' : 'translate-x-1'}`} />
                    </button>
                  </div>
                </div>
                {/* Privacy Policy - read only */}
                <div className="flex items-center justify-between">
                  <span className="text-xs text-gray-400">Privacy Policy</span>
                  <span className={`text-xs font-medium ${profile.privacy_policy_accepted ? 'text-green-400' : 'text-amber-400'}`}>
                    {profile.privacy_policy_accepted ? 'Accepted' : 'Pending'}
                  </span>
                </div>
                {/* Email verified */}
                {profile.is_email_verified !== null && (
                  <div className="flex items-center justify-between">
                    <span className="text-xs text-gray-400">Email verified</span>
                    <span className={`text-xs font-medium ${profile.is_email_verified ? 'text-green-400' : 'text-red-400'}`}>
                      {profile.is_email_verified ? 'Verified' : 'Not verified'}
                    </span>
                  </div>
                )}
                {/* Phone verified — only shown when a phone number exists */}
                {profile.phone && (
                  <div className="flex items-center justify-between">
                    <span className="text-xs text-gray-400">Phone verified</span>
                    <span className={`text-xs font-medium ${profile.is_phone_verified ? 'text-green-400' : 'text-amber-400'}`}>
                      {profile.is_phone_verified ? 'Verified' : 'Not verified'}
                    </span>
                  </div>
                )}
                {/* Agreement signed — only shown when a packet has been sent */}
                {profile.has_signed_agreement !== null && (
                  <div className="flex items-center justify-between">
                    <span className="text-xs text-gray-400">Agreement signed</span>
                    <span className={`text-xs font-medium ${profile.has_signed_agreement ? 'text-green-400' : 'text-amber-400'}`}>
                      {profile.has_signed_agreement ? 'Signed' : 'Pending signature'}
                    </span>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Sessions card */}
          <div className="bg-dark-card border border-dark-border rounded-xl overflow-hidden">
            <div className="px-5 py-4 border-b border-dark-border flex items-center justify-between">
              <h3 className="text-sm font-semibold text-gray-200">Sessions</h3>
              <button onClick={() => navigate(`/dashboard/coach?client=${encodeURIComponent(clientName)}&clientId=${clientId}&rel=${relIdVal}#schedules`)} className="text-xs text-primary-400 hover:text-primary-300">View all →</button>
            </div>
            {loadingAppts ? (
              <div className="px-5 py-6"><Spinner size="sm" /></div>
            ) : sessionGroups.length === 0 ? (
              <p className="px-5 py-6 text-xs text-gray-500">No upcoming sessions.</p>
            ) : (
              <div className="px-4 py-3 space-y-2">
                {sessionGroups.filter(g => g.key !== 'past').map(({ key, label, sessions: grpSessions }) => {
                  const isOpen = sessionsOpen[key] ?? (key === 'today' || key === 'week');
                  return (
                    <div key={key}>
                      <button
                        type="button"
                        onClick={() => setSessionsOpen(p => ({ ...p, [key]: !isOpen }))}
                        className="flex items-center gap-1.5 w-full text-left py-2"
                      >
                        <span className="text-xs font-semibold text-gray-500 uppercase tracking-wider">
                          {label} ({grpSessions.length})
                        </span>
                        <ChevronRightIcon className={`w-3.5 h-3.5 text-gray-500 transition-transform ${isOpen ? 'rotate-90' : ''}`} />
                      </button>
                      {isOpen && (
                        <div className="space-y-1.5 mb-2">
                          {grpSessions.map(s => <SessionRow key={s.id} appt={s} timezone={timezone} onEdit={setEditingAppt} />)}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>

        {/* ── Pending Payments ── */}
        <div className="bg-dark-card border border-dark-border rounded-xl overflow-hidden">
          <div className="px-4 py-3 border-b border-dark-border">
            <h3 className="text-sm font-semibold text-gray-200">Pending Payments</h3>
          </div>
          {loadingOutstanding ? (
            <div className="px-4 py-5"><Spinner size="sm" /></div>
          ) : outstanding.length === 0 ? (
            <p className="px-4 py-5 text-xs text-gray-500">No outstanding payments.</p>
          ) : (
            <div className="divide-y divide-dark-border">
              {outstanding.map((p, i) => {
                const name = p.kind === 'session' ? (p.meeting_name || 'Session') : (p.label || '—');
                const sub  = p.kind === 'session' && p.scheduled_at
                  ? new Date(p.scheduled_at).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' })
                  : p.due_date
                    ? `Due ${new Date(p.due_date).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' })}`
                    : null;
                return (
                  <div key={i} className="px-4 py-3 flex items-center gap-3">
                    <div className="min-w-0 flex-1">
                      <p className="text-sm font-medium text-white truncate">{name}</p>
                      {sub && <p className="text-xs text-gray-500 mt-0.5">{sub}</p>}
                    </div>
                    <span className="text-sm font-semibold text-white flex-shrink-0">
                      {fmtCurrency(p.amount_cents, p.currency)}
                    </span>
                    {p.kind === 'session' && p.appointment_id && (
                      <PayButton appointmentId={p.appointment_id} onPaid={() => {
                        setOutstanding(prev => prev.filter((_, idx) => idx !== i));
                      }} />
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    );
  }

  function renderSessions() {
    if (!profile) return null;
    return (
      <div className="p-8">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-semibold text-white">Sessions</h2>
          <button
            onClick={() => navigate(`/dashboard/coach?schedule=${clientId}`)}
            className="flex items-center gap-2 px-4 py-2 bg-primary-600 hover:bg-primary-700 text-white text-sm font-medium rounded-lg transition-colors"
          >
            + Schedule Session
          </button>
        </div>
        {loadingAppts ? <Spinner size="md" className="py-10" /> : sessionGroups.length === 0 ? (
          <p className="text-sm text-gray-500 italic">No sessions yet.</p>
        ) : (
          <div className="bg-dark-card rounded-xl border border-dark-border shadow-sm">
            <div className="p-5 space-y-6">
              {sessionGroups.map(({ key, label, sessions: grpSessions }) => {
                const collapsible = key === 'past' || key === 'future';
                const isOpen = sessionsOpen[key] ?? (key !== 'past');
                return (
                  <div key={key}>
                    <button
                      type="button"
                      onClick={collapsible ? () => setSessionsOpen(p => ({ ...p, [key]: !isOpen })) : undefined}
                      className={`flex items-center gap-1.5 mb-2 w-full text-left ${collapsible ? 'cursor-pointer' : 'cursor-default'}`}
                    >
                      <p className="text-xs font-semibold text-dark-muted uppercase tracking-wider">
                        {label} <span className="font-normal normal-case">({grpSessions.length})</span>
                      </p>
                      {collapsible && (
                        <ChevronRightIcon className={`w-3.5 h-3.5 text-dark-muted transition-transform ${isOpen ? 'rotate-90' : ''}`} />
                      )}
                    </button>
                    {isOpen && (
                      <div className="space-y-1.5">
                        {grpSessions.map(s => <SessionRow key={s.id} appt={s} timezone={timezone} onEdit={setEditingAppt} />)}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>
    );
  }

  function renderActionItems() {
    return (
      <div className="p-6">
        <ActionItemsPanel
          key={actionItemsTypeFilter || 'all'}
          mode="by_client"
          entityId={clientId!}
          isCoach={true}
          approvedOnly={true}
          clientName={profile?.first_name}
          initialTypeFilter={actionItemsTypeFilter ?? undefined}
          onItemUpdated={() => {
            // Refresh overview counts after any edit
            fetch(`${config.apiBaseUrl}${endpoints.main.actionItems.byClientCoach(clientId!)}`, { headers: authHeaders() })
              .then(r => r.json())
              .then(d => {
                const items: ActionItem[] = d.action_items || [];
                setAllActionItems(items);
                      })
              .catch(() => {});
          }}
        />
      </div>
    );
  }

  function renderSettings() {
    if (!profile) return null;

    const subNavLabel =
      settingsSubSection === 'profile'       ? 'Profile' :
      settingsSubSection === 'notes'         ? 'Custom Notes' :
      settingsSubSection === 'notifications' ? 'Notification Preferences' :
      settingsSubSection === 'privacy'       ? 'Privacy & Data' : '';

    const subNav = settingsSubSection && (
      <div className="flex items-center gap-1.5 text-sm mb-6">
        <button
          onClick={() => setSettingsSubSection(null)}
          className="text-gray-400 hover:text-white transition-colors"
        >
          Settings
        </button>
        <ChevronRightIcon className="w-3.5 h-3.5 text-gray-600 flex-shrink-0" />
        <span className="text-white font-medium">{subNavLabel}</span>
      </div>
    );

    // ── Landing card grid ──────────────────────────────────────────────────────
    if (!settingsSubSection) {
      return (
        <div className="p-8 max-w-2xl space-y-6">
          <h1 className="text-2xl font-bold text-white">Settings</h1>
          <div className="grid grid-cols-2 gap-3">
            {([
              { key: 'profile'       as const, label: 'Profile',                  Icon: UserCircleIcon,            desc: 'Client contact and background information' },
              { key: 'notes'         as const, label: 'Custom Notes',             Icon: ClipboardDocumentListIcon, desc: 'Configure additional sections in AI-generated notes' },
              { key: 'notifications' as const, label: 'Notification Preferences', Icon: BellIcon,                  desc: 'How this client receives each type of notification' },
              { key: 'privacy'       as const, label: 'Privacy & Data',           Icon: ShieldCheckIcon,           desc: 'Export data or remove this client from your practice' },
            ]).map(({ key, label, Icon, desc }) => (
              <button
                key={key}
                onClick={() => setSettingsSubSection(key)}
                className="bg-dark-card border border-dark-border rounded-xl p-4 text-left hover:border-primary-700 hover:bg-dark-hover transition-colors group"
              >
                <Icon className="w-5 h-5 text-gray-500 group-hover:text-primary-400 mb-2 transition-colors" />
                <p className="text-sm font-medium text-white">{label}</p>
                <p className="text-xs text-gray-500 mt-0.5">{desc}</p>
              </button>
            ))}
          </div>

          {/* Delete confirmation modal */}
          {showDeleteClientModal && (
            <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
              <div className="bg-dark-card border border-dark-border rounded-2xl p-6 w-full max-w-md space-y-5">
                <div className="flex items-start justify-between">
                  <h2 className="text-lg font-bold text-white">Remove {profile.first_name} {profile.last_name}?</h2>
                  <button onClick={() => setShowDeleteClientModal(false)} className="text-gray-400 hover:text-white">
                    <XMarkIcon className="w-5 h-5" />
                  </button>
                </div>
                <p className="text-sm text-gray-400">This action is permanent and cannot be undone. The following will be deleted:</p>
                <ul className="text-xs text-gray-400 list-disc list-inside space-y-1">
                  <li>All sessions, session notes, and AI summaries</li>
                  <li>All invoices and payment records</li>
                  <li>All package enrollments and installment payments</li>
                  <li>All action items and nudge schedules</li>
                  <li>All documents and signing records</li>
                  <li>All check-ins and questionnaire responses</li>
                  <li>All client notes and message history</li>
                  <li>The coaching relationship and notification preferences</li>
                </ul>
                <div>
                  <label className="block text-xs font-medium text-gray-300 mb-1">
                    Type <span className="text-white font-semibold">{profile.first_name}</span> to confirm
                  </label>
                  <input
                    type="text"
                    value={deleteClientConfirm}
                    onChange={e => setDeleteClientConfirm(e.target.value)}
                    className="w-full px-3 py-2 bg-gray-800 border border-gray-600 rounded-lg text-sm text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-red-500"
                    placeholder={profile.first_name}
                  />
                </div>
                {deleteClientError && <p className="text-xs text-red-400">{deleteClientError}</p>}
                <div className="flex gap-3 pt-1">
                  <button
                    onClick={() => setShowDeleteClientModal(false)}
                    className="flex-1 px-4 py-2 text-sm border border-dark-border text-gray-300 rounded-lg hover:bg-dark-bg transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleDeleteClient}
                    disabled={deletingClient || deleteClientConfirm !== profile.first_name}
                    className="flex-1 px-4 py-2 text-sm bg-red-700 hover:bg-red-600 disabled:opacity-50 text-white rounded-lg transition-colors"
                  >
                    {deletingClient ? 'Removing…' : 'Yes, remove client'}
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      );
    }

    // ── Profile ────────────────────────────────────────────────────────────────
    if (settingsSubSection === 'profile') {
      return (
        <div className="p-6 max-w-2xl space-y-4">
          {subNav}
          <h2 className="text-lg font-semibold text-white">Profile</h2>
          {!profileEditing ? (
            <div className="bg-dark-card border border-dark-border rounded-2xl p-6 space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <ReadField label="First Name" value={profile.first_name} />
                <ReadField label="Last Name" value={profile.last_name} />
              </div>
              <ReadField label="Email" value={profile.email} />
              {profile.phone && <ReadField label="Phone" value={profile.phone} />}
              {profile.date_of_birth && <ReadField label="Date of Birth" value={profile.date_of_birth} />}
              {(profile.street_address || profile.city) && (
                <ReadField label="Address" value={[profile.street_address, profile.city, profile.state_province, profile.postal_code, profile.country].filter(Boolean).join(', ')} />
              )}
              {profile.occupation && <ReadField label="Occupation" value={profile.occupation} />}
              {profile.employer && <ReadField label="Employer" value={`${profile.employer}${profile.industry ? ` (${profile.industry})` : ''}`} />}
              {profile.bio && <ReadField label="Bio" value={profile.bio} multiline />}
              <div className="pt-2 border-t border-dark-border space-y-2">
                {([
                  { key: 'ai_consent' as const, label: 'AI consent' },
                  { key: 'privacy_policy_accepted' as const, label: 'Privacy Policy' },
                  { key: 'terms_accepted' as const, label: 'Terms of Use' },
                ] as const).map(({ key, label }) => (
                  <div key={key} className="flex items-center justify-between text-sm">
                    <span className="text-gray-400">{label}</span>
                    <span className={`font-medium ${profile[key] === true ? 'text-green-400' : profile[key] === false ? 'text-red-400' : 'text-amber-400'}`}>
                      {profile[key] === true ? 'Accepted' : profile[key] === false ? 'Declined' : 'Pending'}
                    </span>
                  </div>
                ))}
              </div>
              <button
                onClick={() => { setProfileEditing(true); setEditProfile(profile); }}
                className="flex items-center gap-2 text-sm text-primary-400 hover:text-primary-300 transition-colors"
              >
                <PencilIcon className="w-4 h-4" /> Edit Profile
              </button>
            </div>
          ) : (
            <div className="bg-dark-card border border-dark-border rounded-2xl p-6 space-y-4">
              <div className="grid grid-cols-2 gap-3">
                <ProfileField label="First Name" field="first_name" value={editProfile?.first_name || ''} onChange={handleProfileFieldChange} />
                <ProfileField label="Last Name" field="last_name" value={editProfile?.last_name || ''} onChange={handleProfileFieldChange} />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-400 uppercase tracking-wide mb-1">Email</label>
                <p className="text-sm text-gray-500 px-3 py-2 bg-dark-hover rounded-lg border border-dark-border">{profile.email}</p>
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-400 uppercase tracking-wide mb-1">Phone</label>
                <ReactPhoneInput
                  country="us"
                  value={(editProfile?.phone || '').replace(/^\+/, '')}
                  onChange={val => { setEditProfile(p => p ? { ...p, phone: val ? `+${val}` : '' } : p); setProfileDirty(true); setPhoneError(null); }}
                  inputStyle={{ width: '100%', border: `1px solid ${phoneError ? '#ef4444' : '#374151'}`, borderRadius: '0.5rem', fontSize: '0.875rem', height: '38px', background: '#1f2937', color: '#e5e7eb' }}
                  buttonStyle={{ border: `1px solid ${phoneError ? '#ef4444' : '#374151'}`, borderRight: 'none', borderRadius: '0.5rem 0 0 0.5rem', background: '#1f2937' }}
                  enableSearch
                />
              </div>
              <ProfileField label="Date of Birth" field="date_of_birth" value={editProfile?.date_of_birth || ''} onChange={handleProfileFieldChange} type="date" />
              <ProfileField label="Street" field="street_address" value={editProfile?.street_address || ''} onChange={handleProfileFieldChange} />
              <div className="grid grid-cols-2 gap-3">
                <ProfileField label="City" field="city" value={editProfile?.city || ''} onChange={handleProfileFieldChange} />
                <div>
                  <label className="block text-xs font-medium text-gray-400 uppercase tracking-wide mb-1">State / Province</label>
                  {stateOptions.length > 0 ? (
                    <select
                      value={editProfile?.state_province || ''}
                      onChange={e => { setEditProfile(p => p ? { ...p, state_province: e.target.value } : p); setProfileDirty(true); }}
                      className="w-full text-sm text-gray-200 bg-dark-hover border border-dark-border rounded-lg px-3 py-2 focus:outline-none focus:border-primary-500"
                    >
                      <option value="">Select…</option>
                      {stateOptions.map(s => <option key={s.code} value={s.code}>{s.name}</option>)}
                    </select>
                  ) : (
                    <input
                      value={editProfile?.state_province || ''}
                      onChange={e => handleProfileFieldChange('state_province', e.target.value)}
                      className="w-full text-sm text-gray-200 bg-dark-hover border border-dark-border rounded-lg px-3 py-2 focus:outline-none focus:border-primary-500"
                    />
                  )}
                </div>
              </div>
              <ProfileField label="Postal Code" field="postal_code" value={editProfile?.postal_code || ''} onChange={handleProfileFieldChange} />
              <div>
                <label className="block text-xs font-medium text-gray-400 uppercase tracking-wide mb-1">Country</label>
                <select
                  value={editProfile?.country || ''}
                  onChange={e => { setEditProfile(p => p ? { ...p, country: e.target.value, state_province: '' } : p); setProfileDirty(true); }}
                  className="w-full text-sm text-gray-200 bg-dark-hover border border-dark-border rounded-lg px-3 py-2 focus:outline-none focus:border-primary-500"
                >
                  <option value="">Select country…</option>
                  {apiCountries.map(c => <option key={c.iso_code} value={c.iso_code}>{c.name}</option>)}
                </select>
              </div>
              <ProfileField label="Occupation" field="occupation" value={editProfile?.occupation || ''} onChange={handleProfileFieldChange} />
              <div className="grid grid-cols-2 gap-3">
                <ProfileField label="Employer" field="employer" value={editProfile?.employer || ''} onChange={handleProfileFieldChange} />
                <ProfileField label="Industry" field="industry" value={editProfile?.industry || ''} onChange={handleProfileFieldChange} />
              </div>
              <ProfileField label="Bio / Notes" field="bio" value={editProfile?.bio || ''} onChange={handleProfileFieldChange} multiline />

              <div className="border-t border-dark-border pt-4 space-y-3">
                <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide">Consent & Legal</p>
                {([
                  { key: 'ai_consent' as const, label: 'AI session processing', desc: 'Consent to record, transcribe, and summarize sessions using AI' },
                  { key: 'privacy_policy_accepted' as const, label: 'Privacy Policy', desc: 'Accepted the Privacy Policy' },
                  { key: 'terms_accepted' as const, label: 'Terms of Use', desc: 'Accepted the Terms of Use' },
                ] as const).map(({ key, label, desc }) => (
                  <div key={key} className="flex items-start gap-3 p-3 rounded-xl border border-dark-border bg-dark-hover">
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-white">{label}</p>
                      <p className="text-xs text-gray-500 mt-0.5">{desc}</p>
                    </div>
                    <div className="flex gap-2 flex-shrink-0">
                      {[{ v: true, cls: 'bg-green-900/30 border-green-600 text-green-400', lbl: '✓ Yes' }, { v: false, cls: 'bg-red-900/30 border-red-600 text-red-400', lbl: '✗ No' }].map(({ v, cls, lbl }) => (
                        <button
                          key={String(v)}
                          type="button"
                          onClick={() => { setEditProfile(p => p ? { ...p, [key]: v } : p); setProfileDirty(true); }}
                          className={`px-3 py-1.5 text-xs font-medium rounded-lg border transition-colors ${
                            editProfile?.[key] === v ? cls : 'border-dark-border text-gray-500 hover:border-gray-500'
                          }`}
                        >{lbl}</button>
                      ))}
                    </div>
                  </div>
                ))}
              </div>

              <div className="flex items-center gap-3 pt-2">
                <button
                  onClick={handleSaveProfile}
                  disabled={!profileDirty || profileSaving}
                  className="flex items-center gap-2 px-4 py-2 bg-primary-600 hover:bg-primary-700 disabled:opacity-40 text-white text-sm font-medium rounded-lg transition-colors"
                >
                  {profileSaving ? <Spinner size="sm" /> : profileSaved ? <CheckIcon className="w-4 h-4" /> : null}
                  {profileSaving ? 'Saving…' : profileSaved ? 'Saved!' : 'Save Profile'}
                </button>
                <button
                  onClick={() => { setEditProfile(profile); setProfileEditing(false); setProfileDirty(false); }}
                  className="px-4 py-2 text-sm text-gray-400 hover:text-white transition-colors"
                >
                  Cancel
                </button>
                {profileError && <p className="text-sm text-red-400">{profileError}</p>}
              </div>
            </div>
          )}
        </div>
      );
    }

    // ── Custom Notes ───────────────────────────────────────────────────────────
    if (settingsSubSection === 'notes') {
      return (
        <div className="p-6 max-w-2xl space-y-4">
          {subNav}
          <h2 className="text-lg font-semibold text-white">Custom Notes</h2>
          <div className="bg-dark-card border border-dark-border rounded-2xl p-6 space-y-4">
            <p className="text-xs text-gray-500">Configure additional sections to include in AI-generated session notes for this client.</p>
            {extraSections.length === 0 && (
              <p className="text-sm text-gray-500 italic">No custom sections yet. Add one below.</p>
            )}
            {extraSections.map((sec, i) => (
              <div key={sec.id} className="p-4 bg-dark-hover border border-dark-border rounded-xl space-y-3">
                <div className="flex items-center justify-between gap-3">
                  <input
                    value={sec.display_name}
                    onChange={e => setExtraSections(prev => prev.map((s, idx) => idx === i ? { ...s, display_name: e.target.value } : s))}
                    placeholder="Section name…"
                    className="flex-1 text-sm text-gray-200 bg-dark-canvas border border-dark-border rounded-lg px-3 py-1.5 focus:outline-none focus:border-primary-500"
                  />
                  <button onClick={() => setExtraSections(prev => prev.filter((_, idx) => idx !== i))} className="text-xs text-red-400 hover:text-red-300 shrink-0">Remove</button>
                </div>
                <textarea
                  rows={2}
                  value={sec.instructions}
                  onChange={e => setExtraSections(prev => prev.map((s, idx) => idx === i ? { ...s, instructions: e.target.value } : s))}
                  placeholder="Instructions for this section…"
                  className="w-full text-sm text-gray-200 bg-dark-canvas border border-dark-border rounded-lg px-3 py-2 focus:outline-none focus:border-primary-500 resize-none"
                />
                <div className="flex gap-4">
                  <label className="flex items-center gap-2 text-xs text-gray-400 cursor-pointer">
                    <input type="checkbox" checked={sec.enabled} onChange={e => setExtraSections(prev => prev.map((s, idx) => idx === i ? { ...s, enabled: e.target.checked } : s))} className="rounded" />
                    Enabled
                  </label>
                  <label className="flex items-center gap-2 text-xs text-gray-400 cursor-pointer">
                    <input type="checkbox" checked={sec.client_visible} onChange={e => setExtraSections(prev => prev.map((s, idx) => idx === i ? { ...s, client_visible: e.target.checked } : s))} className="rounded" />
                    Visible to client
                  </label>
                </div>
              </div>
            ))}
            <button
              onClick={() => setExtraSections(prev => [...prev, { id: crypto.randomUUID(), display_name: '', instructions: '', enabled: true, client_visible: false }])}
              className="text-sm text-primary-400 hover:text-primary-300 transition-colors"
            >
              + Add section
            </button>
            <div className="flex items-center gap-3 pt-1">
              <button
                onClick={handleSaveNotes}
                disabled={notesSaving}
                className="flex items-center gap-2 px-4 py-2 bg-primary-600 hover:bg-primary-700 disabled:opacity-40 text-white text-sm font-medium rounded-lg transition-colors"
              >
                {notesSaving ? <Spinner size="sm" /> : notesSaved ? <CheckIcon className="w-4 h-4" /> : null}
                {notesSaving ? 'Saving…' : notesSaved ? 'Saved!' : 'Save Notes Config'}
              </button>
              {notesError && <p className="text-sm text-red-400">{notesError}</p>}
            </div>
          </div>
        </div>
      );
    }

    // ── Notification Preferences ───────────────────────────────────────────────
    if (settingsSubSection === 'notifications') {
      return (
        <div className="p-6 max-w-2xl space-y-4">
          {subNav}
          <h2 className="text-lg font-semibold text-white">Notification Preferences</h2>
          <p className="text-sm text-gray-400">How this client receives each type of notification.</p>
          <NotificationPrefsPanel
            events={CLIENT_NOTIF_EVENTS}
            prefs={notifPrefs}
            onChange={(eventType, field, value) =>
              setNotifPrefs(prev => {
                const exists = prev.find(p => p.event_type === eventType);
                if (exists) return prev.map(p => p.event_type === eventType ? { ...p, [field]: value || null } : p);
                return [...prev, { event_type: eventType, channel: 'email', email_override: null, [field]: value }];
              })
            }
            smsIneligible={!profile?.is_phone_verified || !profile?.phone}
            onSave={handleSaveNotif}
            saving={notifSaving}
            saved={notifSaved}
            error={notifError ?? ''}
          />
        </div>
      );
    }

    // ── Privacy & Data ─────────────────────────────────────────────────────────
    if (settingsSubSection === 'privacy') {
      return (
        <div className="p-6 max-w-2xl space-y-4">
          {subNav}
          <h2 className="text-lg font-semibold text-white">Privacy & Data</h2>
          <div className="bg-dark-card border border-dark-border rounded-2xl p-6 space-y-4">
            <div className="space-y-2">
              <p className="text-sm font-medium text-white">Download Client Data</p>
              <p className="text-xs text-gray-400">
                Exports this client's profile, appointments, session notes, action items, check-ins, and documents as a JSON file.
              </p>
              {downloadClientDataError && <p className="text-xs text-red-400">{downloadClientDataError}</p>}
              <button
                onClick={handleDownloadClientData}
                disabled={downloadingClientData}
                className="text-xs bg-primary-600 hover:bg-primary-700 disabled:opacity-50 text-white px-4 py-2 rounded-lg transition-colors"
              >
                {downloadingClientData ? 'Preparing download…' : 'Download Client Data'}
              </button>
            </div>

            <div className="border-t border-red-800/50 pt-4 space-y-2">
              <p className="text-sm font-medium text-red-300">Remove Client</p>
              <p className="text-xs text-gray-400">
                Permanently removes this client from your practice. All sessions, notes, action items, documents, and nudges
                for this relationship will be deleted. The client's account is not affected if they have other coaches.
              </p>
              <button
                onClick={() => { setShowDeleteClientModal(true); setDeleteClientConfirm(''); setDeleteClientError(''); }}
                className="text-xs bg-red-700 hover:bg-red-600 text-white px-4 py-2 rounded-lg transition-colors"
              >
                Remove Client
              </button>
            </div>
          </div>

          {showDeleteClientModal && (
            <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
              <div className="bg-dark-card border border-dark-border rounded-2xl p-6 w-full max-w-md space-y-5">
                <div className="flex items-start justify-between">
                  <h2 className="text-lg font-bold text-white">Remove {profile.first_name} {profile.last_name}?</h2>
                  <button onClick={() => setShowDeleteClientModal(false)} className="text-gray-400 hover:text-white">
                    <XMarkIcon className="w-5 h-5" />
                  </button>
                </div>
                <p className="text-sm text-gray-400">This action is permanent and cannot be undone. The following will be deleted:</p>
                <ul className="text-xs text-gray-400 list-disc list-inside space-y-1">
                  <li>All sessions, session notes, and AI summaries</li>
                  <li>All invoices and payment records</li>
                  <li>All package enrollments and installment payments</li>
                  <li>All action items and nudge schedules</li>
                  <li>All documents and signing records</li>
                  <li>All check-ins and questionnaire responses</li>
                  <li>All client notes and message history</li>
                  <li>The coaching relationship and notification preferences</li>
                </ul>
                <div>
                  <label className="block text-xs font-medium text-gray-300 mb-1">
                    Type <span className="text-white font-semibold">{profile.first_name}</span> to confirm
                  </label>
                  <input
                    type="text"
                    value={deleteClientConfirm}
                    onChange={e => setDeleteClientConfirm(e.target.value)}
                    className="w-full px-3 py-2 bg-gray-800 border border-gray-600 rounded-lg text-sm text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-red-500"
                    placeholder={profile.first_name}
                  />
                </div>
                {deleteClientError && <p className="text-xs text-red-400">{deleteClientError}</p>}
                <div className="flex gap-3 pt-1">
                  <button
                    onClick={() => setShowDeleteClientModal(false)}
                    className="flex-1 px-4 py-2 text-sm border border-dark-border text-gray-300 rounded-lg hover:bg-dark-bg transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleDeleteClient}
                    disabled={deletingClient || deleteClientConfirm !== profile.first_name}
                    className="flex-1 px-4 py-2 text-sm bg-red-700 hover:bg-red-600 disabled:opacity-50 text-white rounded-lg transition-colors"
                  >
                    {deletingClient ? 'Removing…' : 'Yes, remove client'}
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      );
    }

  }

  function renderNotes() {
    const pastWithSummary = appointments.filter(
      a => a.has_summary && new Date(a.scheduled_at) < new Date()
    );
    return (
      <>
        <ClientNotesTab
          coachClientId={String(relIdVal)}
          onGoToSessions={() => setSection('sessions')}
          sessionSummaries={pastWithSummary}
          onViewNotes={appt => setSummaryAppt(appt)}
        />
        {summaryAppt && (
          <SessionSummaryPanel
            appointmentId={summaryAppt.id}
            meetingName={summaryAppt.meeting_name}
            onClose={() => setSummaryAppt(null)}
            role="coach"
            clientName={profile ? `${(profile as any).first_name || ''} ${(profile as any).last_name || ''}`.trim() : undefined}
            coachClientId={String(relIdVal)}
          />
        )}
      </>
    );
  }

  function renderDocuments() {
    if (!profile) return null;
    return (
      <div className="p-6 space-y-6">
        <div className="flex items-center justify-between">
          <h2 className="text-lg font-semibold text-white">Documents</h2>
          <button
            onClick={async () => {
              setSendWelcomeModal(true);
              setWelcomeTitle('Welcome Packet');
              setWelcomeSelectedIds(new Set());
              setWelcomeSendError('');
              setWelcomeSendSuccess('');
              const [docs, tags] = await Promise.all([
                listDocuments().catch(() => [] as DocumentItem[]),
                listTemplateTags().catch(() => [] as DocumentTemplateTag[]),
              ]);
              setWelcomeDocs(docs);
              setWelcomeTagValues(Object.fromEntries(tags.map(t => [t.key, t.default_value])));
            }}
            className="px-4 py-2 bg-primary-600 hover:bg-primary-700 text-white text-sm font-medium rounded-lg transition-colors"
          >
            Send Packet
          </button>
        </div>
        {docsLoading ? <Spinner size="md" className="py-10" /> : docsError ? (
          <p className="text-sm text-red-400">{docsError}</p>
        ) : docPackets.length === 0 ? (
          <p className="text-sm text-gray-500 italic">No document packets sent yet.</p>
        ) : (
          <div className="space-y-3">
            {docPackets.map(pkt => (
              <div key={pkt.id} className="bg-dark-card border border-dark-border rounded-xl overflow-hidden">
                <div className="p-4 flex items-center justify-between gap-4">
                  <div>
                    <p className="text-sm font-medium text-white">{pkt.title}</p>
                    <p className="text-xs text-gray-500 mt-0.5">{pkt.documents?.length ?? 0} document{(pkt.documents?.length ?? 0) !== 1 ? 's' : ''}</p>
                  </div>
                  <span className={`text-xs font-medium px-2.5 py-1 rounded-full ${
                    pkt.status === 'signed' || pkt.status === 'signed_offline' ? 'bg-green-900/30 text-green-400' :
                    pkt.status === 'pending' ? 'bg-amber-900/30 text-amber-400' :
                    'bg-gray-700 text-gray-400'
                  }`}>
                    {pkt.status === 'signed_offline' ? 'Sent & Signed Offline' : pkt.status}
                  </span>
                </div>
                {pkt.documents && pkt.documents.length > 0 && (
                  <div className="border-t border-dark-border divide-y divide-dark-border">
                    {pkt.documents.map(doc => (
                      <div key={doc.document_id} className="px-4 py-2.5 flex items-center justify-between gap-3">
                        <p className="text-xs text-gray-300 truncate">{doc.document_name}</p>
                        <button
                          onClick={async () => {
                            setDocViewLoading(prev => new Set(prev).add(doc.document_id));
                            try {
                              const res = await getDocumentViewUrl(doc.document_id);
                              setDocViewer(res);
                            } finally {
                              setDocViewLoading(prev => { const s = new Set(prev); s.delete(doc.document_id); return s; });
                            }
                          }}
                          disabled={docViewLoading.has(doc.document_id)}
                          className="text-xs text-primary-400 hover:text-primary-300 disabled:opacity-40 transition-colors shrink-0"
                        >
                          {docViewLoading.has(doc.document_id) ? '…' : 'View'}
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}

        {/* Document viewer */}
        {docViewer && (
          <DocumentViewerPanel
            url={docViewer.url}
            fileType={docViewer.file_type}
            docName={docViewer.doc_name}
            onClose={() => setDocViewer(null)}
          />
        )}

        {/* Send packet modal */}
        {sendWelcomeModal && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4">
            <div className="bg-dark-card border border-dark-border rounded-2xl w-full max-w-md p-6 space-y-4">
              <h3 className="text-base font-semibold text-white">Send Document Packet</h3>
              <div>
                <label className="text-xs text-gray-400 uppercase tracking-wide mb-1 block">Packet title</label>
                <input value={welcomeTitle} onChange={e => setWelcomeTitle(e.target.value)} className="w-full text-sm text-gray-200 bg-dark-hover border border-dark-border rounded-lg px-3 py-2 focus:outline-none focus:border-primary-500" />
              </div>
              <div>
                <label className="text-xs text-gray-400 uppercase tracking-wide mb-1 block">Select documents</label>
                {welcomeDocs.map(doc => (
                  <label key={doc.id} className="flex items-center gap-2 py-1 text-sm text-gray-300 cursor-pointer">
                    <input type="checkbox" checked={welcomeSelectedIds.has(doc.id)} onChange={e => setWelcomeSelectedIds(prev => { const s = new Set(prev); e.target.checked ? s.add(doc.id) : s.delete(doc.id); return s; })} className="rounded" />
                    {doc.name}
                  </label>
                ))}
              </div>
              {welcomeSendError && <p className="text-sm text-red-400">{welcomeSendError}</p>}
              {welcomeSendSuccess && <p className="text-sm text-green-400">{welcomeSendSuccess}</p>}
              <div className="flex gap-3 pt-2">
                <button onClick={() => setSendWelcomeModal(false)} className="flex-1 px-4 py-2 border border-dark-border text-gray-400 hover:text-white text-sm rounded-lg transition-colors">Cancel</button>
                <button
                  disabled={welcomeSending || welcomeSelectedIds.size === 0}
                  onClick={async () => {
                    setWelcomeSending(true);
                    setWelcomeSendError('');
                    try {
                      await createPacket(
                        String(relIdVal),
                        welcomeTitle,
                        Array.from(welcomeSelectedIds),
                        undefined,
                        welcomeTagValues,
                      );
                      setWelcomeSendSuccess('Packet sent!');
                      setTimeout(() => { setSendWelcomeModal(false); setDocsLoading(true); listClientFiles(relIdVal).then(setDocPackets).finally(() => setDocsLoading(false)); }, 1500);
                    } catch (e: any) {
                      setWelcomeSendError(e.message || 'Send failed');
                    } finally {
                      setWelcomeSending(false);
                    }
                  }}
                  className="flex-1 px-4 py-2 bg-primary-600 hover:bg-primary-700 disabled:opacity-40 text-white text-sm font-medium rounded-lg transition-colors"
                >
                  {welcomeSending ? 'Sending…' : 'Send'}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  }

  // ── Render ────────────────────────────────────────────────────────────────

  const refetchAppointments = () => {
    if (!clientId) return;
    fetch(`${config.apiBaseUrl}${endpoints.main.clients.sessions(clientId)}?include_payments=true`, { headers: authHeaders() })
      .then(r => r.json())
      .then(d => setAppointments(d.appointments || []))
      .catch(() => {});
  };

  return (
    <>
    <div className="flex bg-dark-canvas" style={{ height: '100vh' }}>

      {/* Sidebar */}
      <aside className="w-56 flex-shrink-0 bg-dark-sidebar border-r border-dark-border flex flex-col">
        {/* Logo */}
        <div className="bg-white border-b border-gray-200 flex-shrink-0">
          <img src="/coachsight-logo.png" alt="CoachSight ECR" style={{ width: 224, height: 72, objectFit: 'cover', objectPosition: 'center 50%' }} />
        </div>

        {/* Back */}
        <div className="px-3 pt-4 pb-2">
          <button
            onClick={() => navigate('/dashboard/coach')}
            className="flex items-center gap-2 text-xs text-gray-500 hover:text-gray-300 transition-colors"
          >
            <ArrowLeftIcon className="w-3.5 h-3.5" /> Back to clients
          </button>
        </div>

        {/* Client identity */}
        <div className="px-3 pb-4 border-b border-dark-border">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-full bg-primary-900/60 flex items-center justify-center flex-shrink-0">
              <span className="text-xs font-semibold text-primary-300">{initials(clientName)}</span>
            </div>
            <div className="min-w-0">
              <p className="text-sm font-semibold text-white truncate">{clientName}</p>
              <p className="text-xs text-gray-500 truncate">{profile.email}</p>
            </div>
          </div>
        </div>

        {/* Nav */}
        <nav className="flex-1 px-3 py-4 space-y-0.5 overflow-y-auto">
          {NAV_ITEMS.map(({ key, label, Icon }) => (
            <button
              key={key}
              onClick={() => {
                if (key === 'sessions') {
                  navigate(`/dashboard/coach?client=${encodeURIComponent(clientName)}&clientId=${clientId}&rel=${relIdVal}#schedules`);
                } else {
                  setSection(key);
                }
              }}
              className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors ${
                section === key
                  ? 'bg-dark-hover text-primary-300'
                  : 'text-gray-400 hover:bg-dark-hover hover:text-gray-200'
              }`}
            >
              <Icon className="w-4 h-4 flex-shrink-0" />
              {label}
            </button>
          ))}
        </nav>
      </aside>

      {/* Main content */}
      <main className="flex-1 overflow-y-auto">
        {section === 'overview'      && renderOverview()}
        {section === 'sessions'      && renderSessions()}
        {section === 'action-items'  && renderActionItems()}
        {section === 'nudges' && (
          <div className="p-6">
            <h2 className="text-lg font-semibold text-white mb-6">Nudges</h2>
            <FollowUpsTab coachClientId={relIdVal} coachToken={localStorage.getItem('authToken') || ''} />
          </div>
        )}
        {section === 'checkins' && (
          <QuestionnairesTab clientId={clientId!} coachClientId={String(relIdVal)} />
        )}
        {section === 'forms' && (
          <ClientFormsTab clientId={clientId!} />
        )}
        {section === 'notes'         && renderNotes()}
        {section === 'documents'     && renderDocuments()}
        {section === 'settings'      && renderSettings()}
      </main>
    </div>

    {editingAppt && (
      <SessionDetailModal
        session={{
          appointmentId: editingAppt.id,
          clientName: clientName,
          date: new Date(editingAppt.scheduled_at).toLocaleDateString(undefined, { weekday: 'short', month: 'short', day: 'numeric', timeZone: timezone }),
          time: new Date(editingAppt.scheduled_at).toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit', timeZone: timezone }),
          duration: editingAppt.actual_duration_minutes || 60,
          appointmentStatus: editingAppt.status,
          googleMeetLink: editingAppt.meeting_url || undefined,
          coachClientId: relIdVal,
        }}
        paymentStatus={null}
        onClose={() => setEditingAppt(null)}
        onRefresh={() => { setEditingAppt(null); refetchAppointments(); }}
        onOpenStripePayment={() => {}}
        onCancelSession={() => { setEditingAppt(null); refetchAppointments(); }}
        onJoinCall={() => editingAppt.meeting_url && openJoinWindow(buildJoinUrl(editingAppt.meeting_url, editingAppt.id, editingAppt.meeting_name))}
      />
    )}
    </>
  );
}

// ── Local helper functions ─────────────────────────────────────────────────────

function buildJoinUrl(meetingUrl: string, appointmentId?: string, name?: string): string {
  const params = new URLSearchParams({ meeting_url: meetingUrl });
  if (appointmentId) params.set('appointment_id', appointmentId);
  if (name) params.set('name', name);
  return `/session/join?${params.toString()}`;
}

function openJoinWindow(url: string): void {
  const w = Math.round(window.screen.width * 0.8);
  const h = Math.round(window.screen.height * 0.8);
  const left = Math.round((window.screen.width - w) / 2);
  const top = Math.round((window.screen.height - h) / 2);
  window.open(url, '_blank', `width=${w},height=${h},left=${left},top=${top},resizable=yes,scrollbars=yes`);
}

// ── Local helper components ────────────────────────────────────────────────────

const PAY_METHODS = [
  { value: 'cash',    label: 'Cash' },
  { value: 'venmo',   label: 'Venmo' },
  { value: 'zelle',   label: 'Zelle' },
  { value: 'paypal',  label: 'PayPal' },
  { value: 'check',   label: 'Check' },
  { value: 'other',   label: 'Other' },
];

function PayButton({ appointmentId, onPaid }: { appointmentId: string; onPaid: () => void }) {
  const [step, setStep]       = React.useState<'idle' | 'picking' | 'paying'>('idle');
  const [method, setMethod]   = React.useState('cash');
  const [done, setDone]       = React.useState(false);

  const handleConfirm = async () => {
    setStep('paying');
    try {
      const { config: cfg } = await import('../config/environment');
      const res = await fetch(
        `${cfg.apiBaseUrl}/coaches/me/sessions/${appointmentId}/mark-paid`,
        {
          method: 'PATCH',
          headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${localStorage.getItem('authToken') || ''}` },
          body: JSON.stringify({ payment_method: method }),
        }
      );
      if (res.ok) { setDone(true); onPaid(); }
      else setStep('picking');
    } catch {
      setStep('picking');
    }
  };

  if (done) return <span className="text-xs text-green-400 font-medium">Paid</span>;
  if (step === 'idle') {
    return (
      <button
        onClick={() => setStep('picking')}
        className="flex-shrink-0 px-3 py-1.5 bg-amber-500 hover:bg-amber-600 text-white text-xs font-semibold rounded-lg transition-colors"
      >
        Pay
      </button>
    );
  }
  return (
    <div className="flex items-center gap-1.5 flex-wrap">
      <select
        value={method}
        onChange={e => setMethod(e.target.value)}
        className="bg-dark-card border border-dark-border rounded px-2 py-1 text-xs text-gray-300 focus:outline-none [color-scheme:dark]"
        autoFocus
      >
        {PAY_METHODS.map(m => <option key={m.value} value={m.value}>{m.label}</option>)}
      </select>
      <button
        onClick={handleConfirm}
        disabled={step === 'paying'}
        className="px-2 py-1 bg-amber-500 hover:bg-amber-600 disabled:opacity-50 text-white text-xs font-semibold rounded transition-colors"
      >
        {step === 'paying' ? '…' : 'Confirm'}
      </button>
      <button
        onClick={() => setStep('idle')}
        className="px-2 py-1 text-xs text-gray-500 hover:text-gray-300 transition-colors"
      >
        ✕
      </button>
    </div>
  );
}

function SessionRow({ appt, timezone, onEdit }: { appt: Appointment; timezone: string; detailed?: boolean; onEdit?: (appt: Appointment) => void }) {
  const navigate = useNavigate();
  const dt = new Date(appt.scheduled_at);
  const timeStr = dt.toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit', timeZone: timezone });
  const dateStr = dt.toLocaleDateString(undefined, { weekday: 'short', month: 'short', day: 'numeric', timeZone: timezone });
  const isPast = dt < new Date();

  const STATUS_CONFIG: Record<string, { label: string; className: string }> = {
    scheduled:   { label: 'Scheduled', className: 'text-gray-400 border-dark-border' },
    completed:   { label: 'Completed', className: 'text-green-400 border-green-800/50' },
    no_show:     { label: 'No-Show',   className: 'text-amber-400 border-amber-800/50' },
    cancelled:   { label: 'Cancelled', className: 'text-red-400 border-red-800/50' },
    in_progress: { label: 'Live',      className: 'text-blue-400 border-blue-800/50' },
  };
  const statusCfg = STATUS_CONFIG[appt.status] ?? STATUS_CONFIG['scheduled'];

  return (
    <div className={`flex items-center justify-between py-3 px-4 rounded-lg border border-dark-border hover:bg-dark-hover transition-colors ${isPast ? 'opacity-70' : ''}`}>
      <div className="flex items-center gap-3 min-w-0">
        <div className="flex-shrink-0 text-right w-20">
          <span className="text-sm font-semibold text-white block">{timeStr}</span>
          <span className="text-xs text-dark-muted">{dateStr}</span>
        </div>
        <div className="w-px h-7 bg-dark-border flex-shrink-0" />
        <VideoCameraIcon className="w-4 h-4 text-gray-400 flex-shrink-0" />
        <div className="min-w-0">
          <button
            onClick={() => navigate(`/dashboard/coach/session/${appt.id}`)}
            className="text-sm font-medium text-white truncate hover:text-primary-300 transition-colors text-left w-full block"
          >
            {appt.meeting_name}
          </button>
          <p className="text-xs text-dark-muted">
            {appt.actual_duration_minutes ? `${appt.actual_duration_minutes} min` : '60 min'}
            {appt.summary_available && <span className="ml-2 text-primary-400">· Notes available</span>}
          </p>
        </div>
      </div>
      <div className="flex items-center gap-1.5 flex-shrink-0">
        <span className={`text-xs border px-2 py-0.5 rounded ${statusCfg.className}`}>{statusCfg.label}</span>
        {appt.meeting_url && !['completed', 'cancelled', 'no_show'].includes(appt.status) && (
          <button
            title="Join meeting"
            onClick={() => openJoinWindow(buildJoinUrl(appt.meeting_url!, appt.id, appt.meeting_name))}
            className="p-1.5 rounded text-primary-400 hover:text-primary-300 hover:bg-dark-hover transition-colors"
          >
            <VideoCameraIcon className="w-4 h-4" />
          </button>
        )}
        {onEdit && (
          <button
            title="Edit session details"
            onClick={() => onEdit(appt)}
            className="p-1.5 rounded text-gray-400 hover:text-gray-200 hover:bg-dark-hover transition-colors"
          >
            <PencilSquareIcon className="w-4 h-4" />
          </button>
        )}
      </div>
    </div>
  );
}

function StatCard({ icon, value, label, accent, tooltip }: { icon: React.ReactNode; value: string; label: string; accent?: 'amber' | 'red' | 'green'; tooltip?: string }) {
  const bg = accent === 'amber' ? 'bg-[#1e1a0e]' : accent === 'red' ? 'bg-[#1e0e0e]' : accent === 'green' ? 'bg-[#0e1a0e]' : 'bg-[#131929]';
  const valClass = accent === 'amber' ? 'text-amber-400' : accent === 'red' ? 'text-red-400' : accent === 'green' ? 'text-green-400' : 'text-white';
  return (
    <div className={`${bg} rounded-2xl p-6 flex flex-col gap-4`}>
      <div className="text-gray-500">{icon}</div>
      <div>
        <p className={`text-4xl font-bold leading-none ${valClass}`}>{value}</p>
        <div className="flex items-center gap-1 mt-2">
          <p className="text-sm text-gray-500">{label}</p>
          {tooltip && (
            <div className="relative group">
              <InformationCircleIcon className="w-3.5 h-3.5 text-gray-600 cursor-default" />
              <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-1.5 hidden group-hover:block z-10 w-52 rounded-lg bg-gray-800 border border-gray-700 px-3 py-2 text-xs text-gray-300 shadow-lg pointer-events-none">
                {tooltip}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}


function ReadField({ label, value, multiline }: { label: string; value: string; multiline?: boolean }) {
  return (
    <div>
      <p className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-0.5">{label}</p>
      {multiline ? (
        <p className="text-sm text-gray-300 whitespace-pre-wrap">{value}</p>
      ) : (
        <p className="text-sm text-gray-200">{value}</p>
      )}
    </div>
  );
}
